import React, { useState, useEffect, useRef, useCallback } from 'react';
import { pdf } from '@react-pdf/renderer';
import { Document, Page, Text, View, Image } from '@react-pdf/renderer';
import { Font } from '@react-pdf/renderer';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../../services/firebase';
import { Analytics } from '../../utils/analytics';

// Noto Sans KR 폰트 등록 — self-host 필수.
// 외부 CDN(gstatic /ea/ 등)은 CORS 헤더 미제공으로 신규 사용자 PDF 생성이 실패함.
Font.register({
  family: 'Noto Sans KR',
  src: `${process.env.PUBLIC_URL}/fonts/NotoSansKR-Regular.ttf`,
  fontStyle: 'normal',
  fontWeight: 'normal'
});

// 템플릿 이미지를 base64로 미리 로드하는 함수
const loadTemplateImage = async (templateUrl) => {
  const imageRef = ref(storage, templateUrl);
  const downloadUrl = await getDownloadURL(imageRef);
  const response = await fetch(downloadUrl);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// PDF 문서 컴포넌트 — imageData는 외부에서 미리 로드하여 전달
const ReportDocument = ({ school, formData, imageData }) => {
  if (!school) return null;

  return (
    <Document>
      <Page size="A4" style={{ padding: 0, margin: 0 }}>
        <View style={{
          position: 'relative',
          width: `${school.printWidth}mm`,
          height: `${school.printHeight}mm`
        }}>
          {imageData && (
            <Image
              src={imageData}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%'
              }}
            />
          )}
          {school.hasFields && Object.entries(formData).map(([field, value]) => {
            if (value && school[`print_${field}Top`] !== undefined) {
              const displayValue = String(value);
              return (
                <Text
                  key={field}
                  style={{
                    position: 'absolute',
                    top: `${school[`print_${field}Top`]}px`,
                    left: `${school.print_left}px`,
                    fontSize: 18,
                    fontFamily: 'Noto Sans KR'
                  }}
                >
                  {field === 'grade' ? `${displayValue}학년` : displayValue}
                </Text>
              );
            }
            return null;
          })}
        </View>
      </Page>
    </Document>
  );
};

// 카운트다운 초
const COUNTDOWN_SECONDS = 7;
const INTERSTITIAL_AD_UNIT = 'DAN-PLRfxTFqgxwpq25w';

// mode: 'download' | 'print'
const AdInterstitial = ({ mode, fileName, blobUrl, onClose }) => {
  const [seconds, setSeconds] = useState(COUNTDOWN_SECONDS);
  const actionTriggered = useRef(false);

  // 생애주기 추적: 마운트 시 'open', 언마운트 시 완료 여부에 따라 'dismissed' 전송.
  useEffect(() => {
    Analytics.adInterstitial('open', INTERSTITIAL_AD_UNIT);
    return () => {
      if (!actionTriggered.current) {
        Analytics.adInterstitial('dismissed', INTERSTITIAL_AD_UNIT);
      }
    };
  }, []);

  const isDownload = mode === 'download';

  // 카운트다운
  useEffect(() => {
    if (seconds <= 0) return;
    const t = setTimeout(() => setSeconds(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds]);

  // 카운트다운 완료 + blob 준비 시 자동 실행
  useEffect(() => {
    if (seconds === 0 && blobUrl && !actionTriggered.current) {
      actionTriggered.current = true;
      Analytics.adInterstitial('completed', INTERSTITIAL_AD_UNIT);

      if (isDownload) {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = fileName;
        a.click();
        onClose();
      } else {
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.src = blobUrl;
        document.body.appendChild(iframe);
        iframe.onload = () => {
          setTimeout(() => {
            iframe.contentWindow.print();
            setTimeout(() => {
              document.body.removeChild(iframe);
              onClose();
            }, 1000);
          }, 500);
        };
      }
    }
  }, [seconds, blobUrl, fileName, onClose, isDownload]);

  // 카카오 애드핏 광고 표시
  const adContainerRef = useRef(null);
  useEffect(() => {
    const container = adContainerRef.current;
    if (!container) return;

    const ins = document.createElement('ins');
    ins.className = 'kakao_ad_area';
    ins.style.display = 'none';
    ins.setAttribute('data-ad-unit', INTERSTITIAL_AD_UNIT);
    ins.setAttribute('data-ad-width', '300');
    ins.setAttribute('data-ad-height', '250');
    container.appendChild(ins);

    const script = document.createElement('script');
    script.src = '//t1.kakaocdn.net/kas/static/ba.min.js';
    script.async = true;
    container.appendChild(script);

    return () => {
      container.innerHTML = '';
    };
  }, []);

  const progress = ((COUNTDOWN_SECONDS - seconds) / COUNTDOWN_SECONDS) * 100;
  const isWaiting = seconds === 0 && !blobUrl;
  const actionLabel = isDownload ? '다운로드' : '인쇄';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        {/* 헤더 */}
        <div className="px-6 pt-6 pb-4 text-center">
          <div className="text-3xl mb-2">{isDownload ? '📄' : '🖨️'}</div>
          <h2 className="text-base font-bold text-gray-800">파일을 준비하고 있어요</h2>
          <p className="text-sm text-gray-500 mt-1">
            {isWaiting
              ? 'PDF를 생성하는 중입니다...'
              : seconds > 0
                ? `${seconds}초 후 자동으로 ${actionLabel}됩니다`
                : `${actionLabel}를 시작합니다!`}
          </p>
        </div>

        {/* 광고 영역 — 카카오 애드핏 */}
        <div
          ref={adContainerRef}
          className="flex justify-center items-center px-4 py-3 bg-gray-50 border-y border-gray-100 min-h-[250px]"
        />

        {/* 카운트다운 + 프로그레스바 */}
        <div className="px-6 pb-6 pt-4">
          <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-indigo-500 to-blue-500 rounded-full transition-all duration-1000 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-center text-xs text-gray-400 mt-3">
            광고 수익으로 서비스를 유지합니다 🙏
          </p>
        </div>
      </div>
    </div>
  );
};

const ActionButtons = ({ school, formData }) => {
  // interstitial: { mode: 'download'|'print', blobUrl: string|null, fileName: string }
  const [interstitial, setInterstitial] = useState(null);

  const handleClose = useCallback(() => {
    setInterstitial(prev => {
      if (prev?.blobUrl) URL.revokeObjectURL(prev.blobUrl);
      return null;
    });
  }, []);

  const handleAction = async (mode) => {
    const fileName = `${school.name}_레포트표지.pdf`;
    setInterstitial({ mode, blobUrl: null, fileName });

    try {
      if (mode === 'download') {
        Analytics.reportDownloaded(school.name);
      } else {
        Analytics.reportPrinted(school.name);
      }
      const imageData = await loadTemplateImage(school.templateUrl);
      const blob = await pdf(
        <ReportDocument school={school} formData={formData} imageData={imageData} />
      ).toBlob();
      const blobUrl = URL.createObjectURL(blob);
      setInterstitial(prev => prev ? { ...prev, blobUrl } : null);
    } catch (error) {
      console.error('PDF 생성 실패:', error);
      setInterstitial(null);
      alert('PDF 생성 중 오류가 발생했습니다.');
    }
  };

  return (
    <>
      {interstitial && (
        <AdInterstitial
          mode={interstitial.mode}
          fileName={interstitial.fileName}
          blobUrl={interstitial.blobUrl}
          onClose={handleClose}
        />
      )}

      <div className="flex gap-4">
        <button
          type="button"
          disabled={!!interstitial}
          onClick={() => handleAction('download')}
          className="inline-flex items-center px-6 py-2.5 text-base font-medium rounded-lg
            text-white bg-gradient-to-r from-indigo-600 to-blue-500
            hover:from-indigo-700 hover:to-blue-600
            shadow-sm hover:shadow-md border-0
            transition-all duration-200 ease-in-out
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
            disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          PDF 다운로드
        </button>

        <button
          type="button"
          disabled={!!interstitial}
          onClick={() => handleAction('print')}
          className="inline-flex items-center px-6 py-2.5 text-base font-medium rounded-lg
            text-white bg-gradient-to-r from-rose-500 to-pink-500
            hover:from-rose-600 hover:to-pink-600
            shadow-sm hover:shadow-md border-0
            transition-all duration-200 ease-in-out
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rose-500
            disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
          </svg>
          인쇄하기
        </button>
      </div>
    </>
  );
};

export default ActionButtons;
